// 全局 ES polyfill：本文件是每个 entry（含 dev_user / prod_user）的第一个 import，
// 因此 polyfill 会最先执行并覆盖所有页面。
// 目标浏览器包含 SketchUp Pro 20 内置的 Chromium 64，它没有 Array.prototype.flatMap、
// Object.fromEntries、Promise.allSettled 等 ES2019+ 能力，这里用 core-js/stable 兜底，
// 业务代码可直接使用这些新 API（core-js 会先判断原生实现，已支持的浏览器不会重复打补丁）。
import "core-js/stable";
import axios from "axios";
import { getRequestUrlParams, getTeamRequestHeaders } from "./request_headers";

const AXIOS_INTERCEPTOR_FLAG = "__teamRequestHeadersInterceptorInstalled__";
const FETCH_INTERCEPTOR_FLAG = "__teamRequestHeadersInterceptorInstalled__";
const XHR_INTERCEPTOR_FLAG = "__teamRequestHeadersInterceptorInstalled__";
const XHR_REQUEST_URL = "__teamRequestHeadersRequestUrl__";

function addAxiosRequestHeaders(config = {}) {
  const requestHeaders = getTeamRequestHeaders(
    config.params,
    config.data,
    getRequestUrlParams(config.url)
  );

  if (!Object.keys(requestHeaders).length) return config;

  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      ...requestHeaders,
    },
  };
}

function installAxiosInterceptor(instance) {
  if (instance[AXIOS_INTERCEPTOR_FLAG]) return instance;

  instance.interceptors.request.use(addAxiosRequestHeaders);
  Object.defineProperty(instance, AXIOS_INTERCEPTOR_FLAG, { value: true });
  return instance;
}

function installAxiosInterceptors() {
  if (axios[AXIOS_INTERCEPTOR_FLAG]) return;

  installAxiosInterceptor(axios);
  const createAxiosInstance = axios.create.bind(axios);
  axios.create = (...args) => installAxiosInterceptor(createAxiosInstance(...args));
}

function mergeFetchHeaders(input, init, requestHeaders) {
  const inputHeaders = typeof Request !== "undefined" && input instanceof Request
    ? input.headers
    : undefined;
  const headers = new Headers(inputHeaders);

  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => headers.set(key, value));
  }
  Object.entries(requestHeaders).forEach(([key, value]) => headers.set(key, value));

  return {
    ...(init || {}),
    headers,
  };
}

function installFetchInterceptor() {
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;
  if (window.fetch[FETCH_INTERCEPTOR_FLAG]) return;

  const originalFetch = window.fetch.bind(window);
  const fetchWithTeamHeaders = (input, init) => {
    const requestHeaders = getTeamRequestHeaders(
      init?.body,
      getRequestUrlParams(input)
    );

    if (!Object.keys(requestHeaders).length) return originalFetch(input, init);
    return originalFetch(input, mergeFetchHeaders(input, init, requestHeaders));
  };

  Object.defineProperty(fetchWithTeamHeaders, FETCH_INTERCEPTOR_FLAG, { value: true });
  window.fetch = fetchWithTeamHeaders;
}

function installXMLHttpRequestInterceptor() {
  if (typeof window === "undefined" || !window.XMLHttpRequest) return;

  const xhrPrototype = window.XMLHttpRequest.prototype;
  if (xhrPrototype[XHR_INTERCEPTOR_FLAG]) return;

  const originalOpen = xhrPrototype.open;
  const originalSend = xhrPrototype.send;

  xhrPrototype.open = function (method, url, ...args) {
    this[XHR_REQUEST_URL] = url;
    return originalOpen.call(this, method, url, ...args);
  };

  xhrPrototype.send = function (body) {
    const requestHeaders = getTeamRequestHeaders(
      body,
      getRequestUrlParams(this[XHR_REQUEST_URL])
    );
    Object.entries(requestHeaders).forEach(([key, value]) => this.setRequestHeader(key, value));
    return originalSend.call(this, body);
  };

  Object.defineProperty(xhrPrototype, XHR_INTERCEPTOR_FLAG, { value: true });
}

installAxiosInterceptors();
installFetchInterceptor();
installXMLHttpRequestInterceptor();
